import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { getMemberSecurity, sendMfaEnrollmentReminder, validateApiResponse } from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { useMemberTabData } from './useMemberTabData';
import { useToastRef } from './useToastRef';

const mapSecurity = ({ mfa, sessions }) => ({
  mfa: {
    enabled: Boolean(mfa?.enabled),
    method: mfa?.method ?? null,
    enrolledAt: mfa?.enrolled_at ?? null,
  },
  sessions: (sessions ?? []).map((s) => ({
    id: s.id,
    device: s.device,
    browser: s.browser,
    location: s.location,
    lastSeenAt: s.last_seen_at ?? null,
    isCurrent: Boolean(s.current),
  })),
});

/**
 * MFA enrollment and active sessions of a member: `{ mfa, sessions, isLoading, error }` plus
 * `sendReminder()`, which asks the member to enroll, toasts the outcome and resolves `true`/`false`.
 * The reminder can be sent again.
 *
 * @param {{ id: string, email: string, status: string }} member UI member
 */
export const useMemberSecurity = (member) => {
  const { t } = useTranslation('people-access');
  const toastRef = useToastRef();
  const memberId = member.id;

  const load = useCallback(async () => {
    const res = validateApiResponse(await getMemberSecurity(memberId), t('memberTabs.loadError'));
    return mapSecurity(res.body);
  }, [memberId, t]);
  const { data, isLoading, error, retry } = useMemberTabData(memberId, load, {
    enabled: member.status !== MEMBER_STATUS.REMOVED,
  });

  const sendReminder = useCallback(async () => {
    try {
      const res = validateApiResponse(
        await sendMfaEnrollmentReminder(memberId),
        t('memberTabs.security.toast.failedTitle'),
      );
      toastRef.current.success({
        title: t('memberTabs.security.toast.reminderTitle'),
        message: t('memberTabs.security.toast.reminderMessage', { email: res.body?.email ?? member.email }),
      });
      return true;
    } catch (err) {
      toastRef.current.error({
        title: t('memberTabs.security.toast.failedTitle'),
        message: err?.message ?? t('memberDetail.toast.unknown'),
      });
      return false;
    }
  }, [memberId, member.email, toastRef, t]);

  return useMemo(
    () => ({
      mfa: data?.mfa ?? null,
      sessions: data?.sessions ?? [],
      isLoading,
      error,
      sendReminder,
      reload: retry,
    }),
    [data, isLoading, error, sendReminder, retry],
  );
};
